import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { forkJoin, interval, map, switchMap, takeUntil, tap } from 'rxjs';
import { PatientConsentService } from 'src/app/core/services/patient-consent.service';
import { PatientAppService } from 'src/app/core/services/patient-app.service';
import { consentAppState, latestConsentRequests, PatientAppConsentRequest } from 'src/app/core/models/patient-app-consent';
import { ConsentFormsService } from 'src/app/core/services/consent-forms.service';
import { PatientConsentDialogComponent } from '../patient-consent-dialog/patient-consent-dialog.component';
import { environment } from './../../../../../environments/environment';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientInviteService, getResendCooldown, formatCooldownRemaining } from './../../../../core/services/patient-invite.service';
import { PatientService } from './../../../../core/services/patient.service';
import { UserService } from './../../../../core/services/user.service';
import { jwtDecode } from 'jwt-decode';
import { inject } from '@angular/core';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
  selector: 'app-patient-consent-list',
  templateUrl: './patient-consent-list.component.html',
  styleUrls: ['./patient-consent-list.component.scss']
})
export class PatientConsentListComponent extends BaseComponent implements OnInit {
  private readonly groupService = inject(GroupService);

  /** A state unit (tasks/state-mode.md): consents are signed at the desk, never sent. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  consentData!: any[] | null;
  showConsentRequired = true;
  patient: any;
  invites?: any[];
  emails?: any[];
  latestInvite: any;
  latestEmail: any;
  /** Ticks every second so cooldown countdown updates. */
  cooldownTick = 0;
  /** The patient app: whether this file is linked, and the newest request of each consent sent there. */
  appLinked = false;
  appRequests: Record<string, PatientAppConsentRequest> = {};

  constructor(
    private router: Router,
    private userService: UserService,
    private patientConsentService: PatientConsentService,
    private patientService: PatientService,
    private patientInviteService: PatientInviteService,
    private consentFormsService: ConsentFormsService,
    private patientAppService: PatientAppService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.patientInviteService.patientInvites$
      .pipe(takeUntil(this.destroy$))
      .subscribe(invites => this.getLatestInvite(invites));


    this.patientInviteService.patientEmails$
      .pipe(takeUntil(this.destroy$))
      .subscribe(emails => this.getLatestEmail(emails));
  }

  ngOnInit(): void {
    this.loadTimelineData();
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cooldownTick = Date.now();
    });
  }

  getResendCooldownState() {
    return getResendCooldown(this.latestInvite?.nextAllowedResendAt);
  }

  getResendCooldownTooltip(): string {
    const c = getResendCooldown(this.latestInvite?.nextAllowedResendAt);
    if (c.canResend) return 'Resend this invite';
    return `Resend available in ${formatCooldownRemaining(c.secondsRemaining)}`;
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  loadTimelineData(): void {
    this.patientConsentService.patientConsents$
      .pipe(
        tap((result) => {
          // A consent signed or declined in the patient app arrives as consentUpdated: its request is read again.
          if (this.patientService.patient?.id) this.loadAppRequests(this.patientService.patient.id);
          if (!this.patient || (this.patient?.id !== this.patientService.patient?.id)) {
            if (result) {
              this.showConsentRequired = true;
              this.patient = this.patientService.patient;
              this.checkConsentProvided(result);
            }
          }
        }),
        map((consents) => {
          return this.sortConsentRecords(consents);
        }),
        takeUntil(this.destroy$),
      ).subscribe({
        next: (result) => {
          this.consentData = result;
        },
        error: (err) => {
          console.error('Error loading consent records:', err);
          this.consentData = [];
        }
      });
  }

  loadAppRequests(patientId: string): void {
    this.patientAppService.consentRequests(patientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (r) => {
          this.appLinked = r.linked;
          this.appRequests = latestConsentRequests(r.requests);
        },
        error: () => {
          this.appLinked = false;
          this.appRequests = {};
        },
      });
  }

  /** What an unsigned consent says about the patient app. Display only: the request is opened automatically with
   *  the invite (patient-platform tasks/patient-email-inbox.md "Consents follow the email"). */
  appState(item: any) {
    return consentAppState(this.appRequests[String(item.id).toLowerCase()]);
  }

  getLatestInvite(invites: any) {
    this.invites = invites;

    if (invites?.length) {
      const filtered = _.chain(invites)
        .filter(inv => moment.utc().isBefore(moment(inv.inviteExpiry)) && inv.consentOnly)
        .orderBy(inv => moment(inv.createdAt), 'desc')
        .value();

      this.latestInvite = filtered[0] || undefined;

    }
  }


  getLatestEmail(emails: any) {
    this.emails = emails;

    if (emails?.length) {
      const filtered = _.chain(emails)
        .filter(e => e.subject.toLowerCase().indexOf('requests your consent') !== -1)
        .orderBy(e => moment(e.createdAt), 'desc')
        .value();

      this.latestEmail = filtered[0] || undefined;

    }
  }

  completeConsent(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    // Prefer shortToken (short link); fallback to inviteToken for older invites
    const token = this.latestInvite.shortToken ?? this.latestInvite.inviteToken;
    const link = `${environment.PATIENT_BASE_URL}/patient-invite/${token}`;
    window.open(link, '_blank');

  }

  sortConsentRecords(consents: any): any[] {

    if (!consents) return [];

    // For each consent, run analyzeConsentConditions on an array of just that consent.
    // Store the returned array on the consent object as `consentConditions`.
    consents.forEach((c: any) => {
      c.consentConditions = this.patientConsentService.analyzeConsentConditions([c]);
    });

    // Group items by formatted date (no need to format date here since we're just grouping)
    const grouped = _.groupBy(consents, item => item.date ? moment(item.date).format('YYYY-MM-DD') : moment(item.createdAt).format('YYYY-MM-DD'));

    // Sort the groups by date in descending order and transform into an array
    return _.chain(grouped)
      .map((value, key) => ({ date: key, items: value }))
      // Use the ISO date string directly for sorting
      .orderBy(group => group.date, 'desc')
      .value();

  }

  checkConsentProvided(consent: any[]) {
    if (!this.showConsentRequired || !this.patient || this.patient.status === 'invite' || this.userService.user?.suppressConsent === true) {
      return;
    }

    if (this.patientConsentService.ignoreList.indexOf(this.patient.id) > -1) {
      return;
    }

    const pendingConsents = consent.filter(c => !c.consentGiven);

    if (!pendingConsents.length) return;

    /* const hasAudioConsent = consent.filter(c => c.ConsentForm.label === 'Audio Consent' && c.consentGiven).length > 0;
    const hasStorageConsent = consent.filter(c => c.ConsentForm.label === 'Storage of Information Consent' && c.consentGiven).length > 0;
    const hasBillingConsent = consent.filter(c => c.ConsentForm.label === 'Billing Policy' && c.consentGiven).length > 0;
    const hasPOPIAConsent = consent.filter(c => c.ConsentForm.label === 'Protection of Personal Information Act (POPIA)' && c.consentGiven).length > 0;

    if (hasAudioConsent && hasStorageConsent && hasBillingConsent && hasPOPIAConsent) {
      return;
    }

    const missingConsents = [];
    if (!hasAudioConsent) {
      missingConsents.push('audio');
    }
    if (!hasStorageConsent) {
      missingConsents.push('storage');
    }
    if (!hasBillingConsent) {
      missingConsents.push('billing');
    }
    if (!hasPOPIAConsent) {
      missingConsents.push('popia');
    } */

    this.showConsentRequired = false;
    const dialogRef = this.dialog.open(PatientConsentDialogComponent, {
      data: {
        pendingConsents,
        type: '',
        lastSent: this.latestEmail,
        nextAllowedResendAt: this.latestInvite?.nextAllowedResendAt ?? undefined,
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        /*  
        result = {
          type, <--either 'new-consent' or 'email-consent'
          patient, <--patient object
          email, <--in case email was updated
          signature, <--base64
        }
        */

        if (!result?.type) {
          this.patientConsentService.ignoreSelected(result?.patient.id)
          return;
        }

        if (result.type === 'new-consent') {
          const formData = new FormData();
          const signatureDataUrl = result.signature;
          delete result.signature;
          formData.append('data', JSON.stringify(result));

          if (signatureDataUrl) {
            const signatureBlob = this.dataURLToBlob(signatureDataUrl);
            formData.append('signature', signatureBlob, 'signature.png');
          }
          if (result.requestedBy) {
          } else {
          } this.createRequiredConsents(formData);
          return;
        }

        if (result.type === 'email-consent') {
          this.sendConsentEmail(result);
          this.patientConsentService.ignoreSelected(result?.patient.id)
        }

      });
  }

  private dataURLToBlob(dataURL: string) {
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  }


  isDate(value: any): boolean {
    return !isNaN(Date.parse(value));
  }

  createRequiredConsents(data: any) {
    this.patientConsentService.updateBulkConsent(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.feedbackService.showToast(`Consent saved successfully.`);
        },
        error: (error) => {
          this.feedbackService.showToast(`There was an error saving the consent record. Please try again.`);
        }
      });
  }


  sendConsentEmail(data: any) {
    if (!this.latestInvite) {
      return this.createNewConsent(data);
    }
    const cooldown = getResendCooldown(this.latestInvite?.nextAllowedResendAt);
    if (!cooldown.canResend) {
      this.showToastMessage(`Resend available in ${formatCooldownRemaining(cooldown.secondsRemaining)}`);
      return;
    }
    const email = data?.email || this.patient.email;
    this.patientInviteService.resendInvite(this.latestInvite?.id, email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage(result?.sentToApp ? 'Sent by email and to the patient app.' : 'Consent request sent successfully.');
          if (result?.nextAllowedResendAt && this.latestInvite) {
            this.latestInvite.nextAllowedResendAt = result.nextAllowedResendAt;
          }
        },
        error: (error) => {
          console.error('Failed to resend patient consent request: ', error);
          const msg = error?.nextAllowedResendAt
            ? `Resend available in ${formatCooldownRemaining(getResendCooldown(error.nextAllowedResendAt).secondsRemaining)}`
            : 'There was an error sending the consent request. Please try again';
          this.showToastMessage(msg);
          if (error?.nextAllowedResendAt && this.latestInvite) {
            this.latestInvite.nextAllowedResendAt = error.nextAllowedResendAt;
          }
        }
      });
  }

  createNewConsent(dialogResult: any) {

    const date: moment.Moment = moment.utc();
    const patient = dialogResult.patient;
    const formItems: any[] = [];
    const requests = [];

    for (let form of dialogResult.consentForms) {
      const data = {
        groupId: patient.groupId,
        consentOnly: true,
        patientId: patient.id,
        formDetails: form,
        consentFormId: form.consentFormId,
        consentGiven: false,
        email: dialogResult.email,
        date: date.startOf('day').toISOString(),
        consentText: form.text,
        physicianId: dialogResult.physicianId

      };
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      formItems.push(data);
      requests.push(this.patientConsentService.addConsent(formData))
    }

    forkJoin(requests)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.patientInviteService.addInvite({ groupId: patient.groupId, email: patient.email, consentOnly: true, patientId: patient.id, formDetails: formItems[0].formDetails, physicianId: dialogResult.physicianId }, true))
      )
      .subscribe({
        next: (result) => {
          this.showToastMessage(result?.sentToApp ? 'Sent by email and to the patient app.' : 'Patient consent sent successfully');
        },
        error: (error) => {
          console.error('Error sending patient consent: ', error);
          this.showToastMessage('There was an error sending the patient consent. Please try again.');
        }
      });

  }

  loadConsent(data: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.patientConsentService.setEditingConsent(data);
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['edit-consent', data.id],
      }
    }]);
  }

  async downloadConsentPDF(item: any, event?: MouseEvent): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (!item.consentGiven && this.patient) {
      try {
        // Prepare consent data in the format expected by the service
        const consentData = {
          consentLabel: item.ConsentForm?.label || '',
          consentText: item.consentText || item.ConsentForm?.text || '',
          form: item.ConsentForm || {},
          confirmSide: item.ConsentForm?.confirmSide,
          sideOptions: item.ConsentForm?.sideOptions,
          confirmImplant: item.ConsentForm?.confirmImplant,
          implantOptions: item.ConsentForm?.implantOptions,
          confirmTransfusion: item.ConsentForm?.confirmTransfusion,
          transfusionOptions: item.ConsentForm?.transfusionOptions,
          additionalRequiredConsent: item.ConsentForm?.additionalRequiredConsent || [],
          selectedProcedureSide: item.consentConditions?.procedureSide,
          selectedImplant: item.consentConditions?.selectedImplant,
          selectedTransfusion: item.consentConditions?.bloodTransfusion,
          selectedRequiredConsent: item.consentConditions?.items?.find((i: any) => i.category === 'required')?.text,
          conditionalConsent: (item.ConsentForm?.conditionalConsent || []).map((consent: string) => ({
            name: consent,
            consentGiven: item.consentConditions?.items?.some((i: any) => i.text === consent && i.category === 'optional')
          }))
        };
        
        await this.consentFormsService.generateConsentPDF(consentData, this.patient);
      } catch (error) {
        console.error('Error generating consent PDF:', error);
        this.feedbackService.showToast('There was an error generating the PDF. Please try again.');
      }
    }
  }

}
